import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { PropertyListener } from '@vsolv/dev-kit/rx';
import { PropertySet } from '@vsolv/packages/properties/domain';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { Policy } from '@wsphere/warranties/domain';
import { BehaviorSubject, combineLatest, tap } from 'rxjs';
import { PolicyService } from '../../services';

export interface PolicyAsset {
  propertySet: PropertySet.Model | null;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-policy-asset',
  templateUrl: './policy-asset.component.html',
  standalone: false,
})
export class PolicyAssetComponent {
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private toastSvc: ToastService,
    private policySvc: PolicyService,
    private formBuilder: FormBuilder
  ) {}

  @PropertyListener('policy') policy$ = new BehaviorSubject<Policy.Model | null>(null);
  @Input() policy: Policy.Model | null = null;

  @PropertyListener('editing') editing$ = new BehaviorSubject<boolean>(false);
  @Input() editing = false;

  @Input() value: PolicyAsset | null = null;

  @Output() closed = new EventEmitter();
  @Output() prevPage = new EventEmitter<PolicyAsset>();
  @Output() pageCompleted = new EventEmitter<PolicyAsset>();

  saving = false;

  draft = Policy.Status.DRAFT;

  form = this.formBuilder.group({
    propertySet: [null as PropertySet.Model | null, [Validators.required]],
  });

  patchValue$ = combineLatest([this.policy$, this.editing$]).pipe(
    tap(([policy, editing]) => this.loadForm(policy, editing))
  );

  close() {
    // Reload from the current inputs instead of overwriting them: the dialog's [policy]/[editing]
    // bindings don't change between opens, so Angular would never set them back and the next Edit
    // would reopen in create mode with a blank form.
    this.reload();

    this.closed.emit();
  }

  /** Reload the form from the current inputs, dropping any unsaved edits. */
  reload() {
    this.form.reset();
    this.loadForm(this.policy, this.editing);
  }

  /** Edit mode shows the policy's saved property set; create mode restores the wizard's value. */
  private loadForm(policy: Policy.Model | null, editing: boolean) {
    if (!editing) {
      if (this.value) {
        this.form.patchValue(this.value);
        this.form.markAsDirty();
      }
    } else {
      if (!policy) return;
      this.form.patchValue({ propertySet: policy.propertySet });
    }
  }

  goBack() {
    this.prevPage.emit({
      propertySet: this.form.value.propertySet || null,
    });
  }

  pageComplete() {
    this.pageCompleted.emit({
      propertySet: this.form.value.propertySet || null,
    });
  }

  async save() {
    if (!this.policy) return;
    this.saving = true;
    const propertySetId = this.form.value.propertySet?.id;
    if (!propertySetId || propertySetId.trim() === '') {
      this.toastSvc.show({
        type: 'error',
        title: 'Property Set Required!',
        text: 'Please select a property set and try again',
      });
      this.saving = false;
      return;
    }
    await this.policySvc
      .update(this.policy.id, {
        propertySetId,
        canCreateNewVersion: false,
      })
      .then(() => {
        this.toastSvc.show({
          type: 'success',
          title: 'Property set saved',
          text: 'The property set has been successfully saved to the policy.',
        });

        // The dialog reloads this editor from the policy on every open, so it must hold the saved set.
        this.policySvc.refreshPolicy();
      })
      .catch(({ error }) => {
        this.toastSvc.show({
          type: 'error',
          title: 'Failed to save property set',
          text: error.message || 'An error occurred while saving the property set.',
        });
      });
    this.saving = false;
    this.closed.emit();
  }

  navigateTo(path: string) {
    this.router.navigate([`${path}`], { relativeTo: this.route });
  }
}
